<template>
  <div class="sensor-history">
    <AppHeader :title="sensorTitle" />

    <!-- 时间选择区域 -->
    <div class="time-picker-section">
      <!-- 快速时间选择 -->
      <div class="quick-time-row">
        <div class="quick-btn" :class="{ active: quickDays === 1 }" @click="setQuickTime(1)">1天</div>
        <div class="quick-btn" :class="{ active: quickDays === 7 }" @click="setQuickTime(7)">7天</div>
        <div class="quick-btn" :class="{ active: quickDays === 30 }" @click="setQuickTime(30)">30天</div>
        <div class="quick-btn" :class="{ active: quickDays === 365 }" @click="setQuickTime(365)">1年</div>
      </div>
      <!-- 时间详情折叠 -->
      <div class="time-detail-toggle" @click="showTimeDetail = !showTimeDetail">
        <span class="time-summary">{{ formatTimeRange() }}</span>
        <span class="toggle-icon" :class="{ expanded: showTimeDetail }">▼</span>
      </div>
      <div class="time-detail" v-show="showTimeDetail">
        <div class="time-row">
          <label>开始时间</label>
          <input type="datetime-local" v-model="beginAt" class="time-input" @change="quickDays = 0" />
        </div>
        <div class="time-row">
          <label>结束时间</label>
          <input type="datetime-local" v-model="endAt" class="time-input" @change="quickDays = 0" />
        </div>
      </div>
      <button class="query-btn" @click="queryData">查询</button>
    </div>

    <!-- 视图切换 -->
    <div class="view-switch" v-if="canShowChart">
      <div class="switch-btn" :class="{ active: viewMode === 'chart' }" @click="viewMode = 'chart'">曲线图</div>
      <div class="switch-btn" :class="{ active: viewMode === 'table' }" @click="viewMode = 'table'">表格</div>
    </div>

    <!-- 曲线图区域 -->
    <div class="chart-section" v-if="viewMode === 'chart' && canShowChart">
      <div class="chart-title">{{ chartTitle }}</div>
      <canvas ref="chartCanvas" class="chart-canvas"></canvas>
      <div class="chart-empty" v-if="chartData.length === 0 && !loading">暂无数据</div>
    </div>

    <!-- 数据列表 -->
    <div class="data-list-section" v-if="viewMode === 'table'">
      <div class="list-header">
        <span>历史数据</span>
        <span class="total-count" v-if="total > 0">共 {{ total }} 条</span>
      </div>
      <div class="data-list" v-if="dataList.length > 0">
        <div class="data-item" v-for="(item, idx) in dataList" :key="idx">
          <div class="data-value">{{ item.value }}</div>
          <div class="data-time">{{ formatTime(item.happenAt) }}</div>
        </div>
      </div>
      <div class="empty-state" v-else-if="!loading">
        暂无数据
      </div>

      <!-- 加载更多 -->
      <div class="load-more" v-if="hasMore" @click="loadMore">
        {{ loadingMore ? '加载中...' : '加载更多' }}
      </div>
    </div>

    <!-- 加载状态 -->
    <div class="loading-overlay" v-if="loading">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>
  </div>
</template>

<script>
import AppHeader from "@/components/AppHeader";
import { Toast } from 'mint-ui';

export default {
  components: {
    AppHeader
  },
  data() {
    return {
      sensorId: null,
      equipmentId: null,
      sensorCode: '',
      sensorName: '',
      uniqueId: '',
      sensorVersion: 1,
      sensorTitle: '传感器历史数据',
      chartTitle: '',
      beginAt: '',
      endAt: '',
      quickDays: 1, // 快速选择的天数
      showTimeDetail: false, // 是否显示时间详情
      viewMode: 'chart', // chart 或 table
      canShowChart: true, // 是否可以显示曲线图
      chartData: [], // 图表数据
      dataList: [],
      loading: false,
      loadingMore: false,
      page: 1,
      pageSize: 20,
      total: 0,
      hasMore: false
    }
  },
  methods: {
    // 初始化默认时间（当前时间-1天 到 当前时间）
    initDefaultTime() {
      this.setQuickTime(1);
    },

    // 快速设置时间范围
    setQuickTime(days) {
      this.quickDays = days;
      const now = new Date();
      const pastDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

      this.endAt = this.formatDateTimeLocal(now);
      this.beginAt = this.formatDateTimeLocal(pastDate);
    },

    // 格式化时间范围显示
    formatTimeRange() {
      if (!this.beginAt || !this.endAt) return '选择时间范围';
      const begin = this.beginAt.replace('T', ' ');
      const end = this.endAt.replace('T', ' ');
      return `${begin} 至 ${end}`;
    },

    // 格式化为 datetime-local 输入框格式
    formatDateTimeLocal(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${d}T${h}:${min}`;
    },

    // 格式化显示时间
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}`;
    },

    // 格式化为API请求格式 (yyyy-MM-dd HH:mm)
    formatDateTimeApi(dateTimeLocal) {
      if (!dateTimeLocal) return '';
      return dateTimeLocal.replace('T', ' ');
    },

    // 查询传感器类型
    async querySensorType() {
      try {
        const res = await this.$http.get(`/portal/report/chart/equipment/sensorQuery?sensorId=${this.sensorId}`, { indicator: false });
        if (res && res.success && res.data) {
          // type=2 可以显示曲线图，type=3 不能显示曲线图
          this.canShowChart = res.data.type === 2;
          if (!this.canShowChart) {
            this.viewMode = 'table';
          }
        }
      } catch (e) {
        console.error('查询传感器类型失败', e);
      }
    },

    // 查询数据
    async queryData() {
      this.loading = true;
      this.page = 1;
      this.dataList = [];
      this.chartData = [];

      try {
        // 并行查询图表数据和列表数据
        await Promise.all([
          this.canShowChart ? this.queryChartData() : Promise.resolve(),
          this.queryListData()
        ]);
      } catch (e) {
        console.error('查询数据失败', e);
      } finally {
        this.loading = false;
      }
    },

    // 查询图表数据
    async queryChartData() {
      if (!this.sensorCode || !this.sensorName) {
        console.log('缺少 sensorCode 或 sensorName，无法查询图表数据');
        return;
      }

      try {
        const beginAt = this.formatDateTimeApi(this.beginAt);
        const endAt = this.formatDateTimeApi(this.endAt);

        let url = `/portal/report/chart/equipment/sensor?equipmentId=${this.equipmentId}&sensorCode=${encodeURIComponent(this.sensorCode)}&sensorName=${encodeURIComponent(this.sensorName)}&beginAt=${encodeURIComponent(beginAt)}&endAt=${encodeURIComponent(endAt)}`;

        // 如果有 uniqueId，使用 2.0 版本接口
        if (this.uniqueId && this.sensorVersion === 2) {
          url += `&uniqueId=${encodeURIComponent(this.uniqueId)}&sensorVersion=2`;
        }

        console.log('查询图表数据:', url);
        const res = await this.$http.get(url, { indicator: false });
        console.log('图表数据结果:', res);

        if (res && res.dataMapAry) {
          this.chartData = res.dataMapAry || [];
          this.chartTitle = (res.title || '传感器') + '数据变化';
          this.$nextTick(() => {
            this.drawChart();
          });
        }
      } catch (e) {
        console.error('查询图表数据失败', e);
      }
    },

    // 绘制图表
    drawChart() {
      const canvas = this.$refs.chartCanvas;
      if (!canvas || this.chartData.length === 0) return;

      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      // 设置 canvas 尺寸
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);

      const width = rect.width;
      const height = rect.height;
      const padding = { top: 20, right: 20, bottom: 40, left: 50 };
      const chartWidth = width - padding.left - padding.right;
      const chartHeight = height - padding.top - padding.bottom;

      // 清空画布
      ctx.clearRect(0, 0, width, height);

      // 提取数据
      const data = this.chartData.map(item => ({
        time: new Date(item.value[0]),
        value: parseFloat(item.value[1])
      })).filter(d => !isNaN(d.value));

      if (data.length === 0) return;

      // 计算数据范围
      const values = data.map(d => d.value);
      let minValue = Math.min(...values);
      let maxValue = Math.max(...values);

      // 添加一些边距
      const valueRange = maxValue - minValue || 1;
      minValue -= valueRange * 0.1;
      maxValue += valueRange * 0.1;

      const minTime = data[0].time.getTime();
      const maxTime = data[data.length - 1].time.getTime();
      const timeRange = maxTime - minTime || 1;

      // 绘制网格
      ctx.strokeStyle = '#eee';
      ctx.lineWidth = 1;

      // 水平网格线
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding.left, y);
        ctx.lineTo(width - padding.right, y);
        ctx.stroke();
      }

      // Y轴标签
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (chartHeight / 5) * i;
        const value = maxValue - (valueRange * 1.2 / 5) * i;
        ctx.fillText(value.toFixed(1), padding.left - 5, y + 3);
      }

      // 绘制折线
      ctx.strokeStyle = '#1890ff';
      ctx.lineWidth = 2;
      ctx.beginPath();

      data.forEach((d, i) => {
        const x = padding.left + ((d.time.getTime() - minTime) / timeRange) * chartWidth;
        const y = padding.top + ((maxValue - d.value) / (maxValue - minValue)) * chartHeight;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();

      // 绘制数据点
      ctx.fillStyle = '#1890ff';
      data.forEach(d => {
        const x = padding.left + ((d.time.getTime() - minTime) / timeRange) * chartWidth;
        const y = padding.top + ((maxValue - d.value) / (maxValue - minValue)) * chartHeight;

        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });

      // X轴时间标签
      ctx.fillStyle = '#999';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';

      const labelCount = Math.min(5, data.length);
      for (let i = 0; i < labelCount; i++) {
        const idx = Math.floor((data.length - 1) * i / (labelCount - 1 || 1));
        const d = data[idx];
        const x = padding.left + ((d.time.getTime() - minTime) / timeRange) * chartWidth;
        const timeStr = this.formatTime(d.time.getTime());
        ctx.fillText(timeStr.split(' ')[1] || timeStr, x, height - 10);
      }
    },

    // 查询列表数据
    async queryListData() {
      try {
        const beginAt = this.formatDateTimeApi(this.beginAt);
        const endAt = this.formatDateTimeApi(this.endAt);
        // 后端 P.fix() 会将 page-1，所以前端 page 从 1 开始
        const url = `/portal/report/equipment/sensor/count?sensorId=${this.sensorId}&beginAt=${encodeURIComponent(beginAt)}&endAt=${encodeURIComponent(endAt)}&page=${this.page}&size=${this.pageSize}`;

        console.log('查询传感器历史数据:', url);
        const res = await this.$http.get(url, { indicator: false });
        console.log('查询结果:', res);

        if (res && res.success && res.data) {
          const content = res.data.content || [];
          this.total = res.data.totalElements || 0;

          if (this.page === 1) {
            this.dataList = content;
          } else {
            this.dataList = this.dataList.concat(content);
          }

          this.hasMore = this.dataList.length < this.total;
        } else if (res && !res.success) {
          Toast(res.msg || '查询失败');
        }
      } catch (e) {
        console.error('查询列表数据失败', e);
        Toast('查询失败，请重试');
      }
    },

    // 加载更多
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;

      this.loadingMore = true;
      this.page++;

      try {
        await this.queryListData();
      } finally {
        this.loadingMore = false;
      }
    }
  },
  watch: {
    viewMode(newVal) {
      if (newVal === 'chart' && this.chartData.length > 0) {
        this.$nextTick(() => {
          this.drawChart();
        });
      }
    }
  },
  mounted() {
    // 从路由参数获取传感器信息
    const query = this.$route.query;
    this.sensorId = query.sensorId;
    this.equipmentId = query.equipmentId;
    this.sensorCode = query.sensorCode || '';
    this.sensorName = query.sensorName || '';
    this.uniqueId = query.uniqueId || '';
    this.sensorVersion = parseInt(query.sensorVersion) || 1;
    this.sensorTitle = query.title || '传感器历史数据';

    if (!this.sensorId) {
      Toast('缺少传感器ID');
      this.$router.back();
      return;
    }

    // 初始化默认时间
    this.initDefaultTime();

    // 查询传感器类型，然后加载数据
    this.querySensorType().then(() => {
      this.queryData();
    });
  }
}
</script>

<style lang="scss" scoped>
.sensor-history {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

.time-picker-section {
  background: #fff;
  padding: 15px;

  .quick-time-row {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;

    .quick-btn {
      flex: 1;
      padding: 8px 0;
      text-align: center;
      font-size: 13px;
      color: #666;
      background: #f5f5f5;
      border-radius: 4px;
      border: 1px solid #eee;

      &.active {
        color: #fff;
        background: #1890ff;
        border-color: #1890ff;
      }

      &:active {
        opacity: 0.8;
      }
    }
  }

  .time-detail-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: #f9f9f9;
    border-radius: 4px;
    margin-bottom: 10px;

    .time-summary {
      font-size: 12px;
      color: #666;
    }

    .toggle-icon {
      font-size: 10px;
      color: #999;
      transition: transform 0.2s;

      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .time-detail {
    .time-row {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      label {
        width: 70px;
        font-size: 14px;
        color: #333;
      }

      .time-input {
        flex: 1;
        padding: 8px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        color: #333;
      }
    }
  }

  .query-btn {
    width: 100%;
    padding: 10px;
    background: #1890ff;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    margin-top: 5px;

    &:active {
      background: #40a9ff;
    }
  }
}

.view-switch {
  display: flex;
  background: #fff;
  margin-top: 10px;
  border-bottom: 1px solid #eee;

  .switch-btn {
    flex: 1;
    padding: 12px;
    text-align: center;
    font-size: 14px;
    color: #666;
    position: relative;

    &.active {
      color: #1890ff;
      font-weight: 500;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 2px;
        background: #1890ff;
      }
    }

    &:active {
      background: #f5f5f5;
    }
  }
}

.chart-section {
  background: #fff;
  padding: 15px;
  margin-bottom: 10px;

  .chart-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    text-align: center;
    margin-bottom: 10px;
  }

  .chart-canvas {
    width: 100%;
    height: 250px;
    display: block;
  }

  .chart-empty {
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
  }
}

.data-list-section {
  background: #fff;

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    font-size: 15px;
    font-weight: 500;
    color: #333;
    border-bottom: 1px solid #eee;

    .total-count {
      font-size: 13px;
      font-weight: normal;
      color: #999;
    }
  }

  .data-list {
    .data-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 15px;
      border-bottom: 1px solid #f0f0f0;

      .data-value {
        font-size: 15px;
        color: #333;
        font-weight: 500;
      }

      .data-time {
        font-size: 13px;
        color: #999;
      }
    }
  }

  .empty-state {
    padding: 40px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }

  .load-more {
    padding: 15px;
    text-align: center;
    color: #1890ff;
    font-size: 14px;

    &:active {
      background: #f5f5f5;
    }
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f0f0f0;
    border-top-color: #1890ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .loading-text {
    margin-top: 10px;
    color: #666;
    font-size: 14px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
